import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Shield,
  Activity,
  TrendingUp,
  Package,
  Tag,
  FolderOpen,
  Clock,
  Mail,
  ShoppingBag,
  AlertTriangle,
} from "lucide-react";
import api from "../../config/api";
import RevenueMetricsCard from "./analytics/RevenueMetricsCard";
import OrderStatusChart from "./analytics/OrderStatusChart";
import InventoryAlerts from "./analytics/InventoryAlerts";
import CustomerMetricsCard from "./analytics/CustomerMetricsCard";
import ProductPerformanceWidget from "./analytics/ProductPerformanceWidget";
import CartAnalyticsWidget from "./analytics/CartAnalyticsWidget";

const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [lostOrdersOpen, setLostOrdersOpen] = useState(0);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    api
      .get("/admin/lost-payments/stats")
      .then((res) => setLostOrdersOpen(res.data?.data?.openCount || 0))
      .catch(() => {});
  }, []);

  const fetchAllData = async () => {
    // Fetch basic stats
    try {
      const response = await api.get("/admin/dashboard/stats");
      setStats(response.data.data.stats);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }

    // Fetch comprehensive analytics
    try {
      const analyticsResponse = await api.get("/admin/dashboard/analytics?period=30");
      setAnalytics(analyticsResponse.data.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setAnalyticsLoading(false);
    }

    // Fetch activity feed
    try {
      const activityResponse = await api.get("/admin/dashboard/activity?limit=10");
      setActivity(activityResponse.data.data);
    } catch (error) {
      console.error("Error fetching activity:", error);
    } finally {
      setActivityLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) {
      return "just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? "s" : ""} ago`;
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "order":
        return <Package className="w-4 h-4" />;
      case "user":
        return <Users className="w-4 h-4" />;
      case "inventory":
        return <Package className="w-4 h-4" />;
      case "payment":
        return <TrendingUp className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case "order":
        return "bg-blue-400";
      case "user":
        return "bg-green-400";
      case "inventory":
        return "bg-yellow-400";
      case "payment":
        return "bg-purple-400";
      default:
        return "bg-gray-400";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const statCards = [
    {
      name: "Total Users",
      value: stats?.totalUsers || 0,
      icon: Users,
      color: "bg-blue-500",
    },
    {
      name: "Customers",
      value: stats?.customers || 0,
      icon: Users,
      color: "bg-green-500",
    },
    {
      name: "Moderators",
      value: stats?.moderators || 0,
      icon: Shield,
      color: "bg-orange-500",
    },
    {
      name: "Admins",
      value: stats?.admins || 0,
      icon: Shield,
      color: "bg-purple-500",
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Comprehensive analytics and system overview
        </p>
      </div>

      {lostOrdersOpen > 0 && (
        <Link
          to="/admin/lost-orders"
          className="mb-6 block rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 hover:bg-amber-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">
              <strong>{lostOrdersOpen} lost payment case{lostOrdersOpen !== 1 ? "s" : ""}</strong>
              {" "}require review — payments captured without a matching order.
            </p>
          </div>
        </Link>
      )}

      {/* User Statistics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {statCards.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.name}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div
                      className={`w-8 h-8 ${item.color} rounded-md flex items-center justify-center`}
                    >
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {item.name}
                      </dt>
                      <dd className="text-2xl font-semibold text-gray-900">
                        {item.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Analytics Dashboard */}
      {!analyticsLoading && analytics && (
        <>
          {/* Key Metrics Row */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-6">
            <RevenueMetricsCard salesData={analytics.sales} />
            <CustomerMetricsCard />
          </div>

          {/* Charts and Widgets Row */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 mb-6">
            <OrderStatusChart orderData={analytics.orders} />
            <ProductPerformanceWidget productData={analytics.products} />
            <CartAnalyticsWidget cartData={analytics.carts} />
          </div>

          {/* Inventory Alerts */}
          <div className="mb-6">
            <InventoryAlerts />
          </div>
        </>
      )}

      {analyticsLoading && (
        <div className="bg-white shadow rounded-lg p-12 mb-6">
          <div className="flex justify-center items-center">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      )}

      {/* Quick Actions and Activity Feed */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Quick Actions
          </h3>
          <div className="space-y-3">
            <Link
              to="/admin/users"
              className="flex items-center p-3 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Users className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-medium">Manage Users</p>
                <p className="text-gray-500">
                  View, edit, and manage user accounts
                </p>
              </div>
            </Link>
            <Link
              to="/admin/products"
              className="flex items-center p-3 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Package className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-medium">Manage Products</p>
                <p className="text-gray-500">
                  Add, edit, and manage product catalog
                </p>
              </div>
            </Link>
            <Link
              to="/admin/categories"
              className="flex items-center p-3 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <FolderOpen className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-medium">Manage Categories</p>
                <p className="text-gray-500">
                  Organize products with categories
                </p>
              </div>
            </Link>
            <Link
              to="/admin/tags"
              className="flex items-center p-3 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Tag className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-medium">Manage Tags</p>
                <p className="text-gray-500">Create and manage product tags</p>
              </div>
            </Link>
            <Link
              to="/admin/orders"
              className="flex items-center p-3 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Package className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-medium">Manage Orders</p>
                <p className="text-gray-500">View and manage customer orders</p>
              </div>
            </Link>
            <Link
              to="/admin/coupons"
              className="flex items-center p-3 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Tag className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-medium">Coupons</p>
                <p className="text-gray-500">Manage discount codes and usage limits</p>
              </div>
            </Link>
            <Link
              to="/admin/lost-orders"
              className="flex items-center p-3 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <AlertTriangle className="mr-3 h-5 w-5 text-amber-500" />
              <div>
                <p className="font-medium">Lost Orders</p>
                <p className="text-gray-500">
                  Find and recover payments without orders
                  {lostOrdersOpen > 0 && (
                    <span className="ml-1 text-amber-600 font-medium">
                      ({lostOrdersOpen} open)
                    </span>
                  )}
                </p>
              </div>
            </Link>
            <Link
              to="/admin/sales"
              className="flex items-center p-3 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <TrendingUp className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-medium">Sales Analytics</p>
                <p className="text-gray-500">
                  Detailed sales reports and trends
                </p>
              </div>
            </Link>
            <Link
              to="/admin/newsletter"
              className="flex items-center p-3 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Mail className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-medium">Newsletter</p>
                <p className="text-gray-500">
                  Build and send newsletters to customers
                </p>
              </div>
            </Link>
            <Link
              to="/admin/abandoned-carts"
              className="flex items-center p-3 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <ShoppingBag className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-medium">Abandoned Carts</p>
                <p className="text-gray-500">
                  List and email customers who left items in cart
                </p>
              </div>
            </Link>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Recent Activity
            </h3>
            <Clock className="w-5 h-5 text-gray-400" />
          </div>
          {activityLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="w-6 h-6 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : activity && activity.activities && activity.activities.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activity.activities.map((item, index) => (
                <div key={index} className="flex items-start text-sm">
                  <div
                    className={`w-2 h-2 ${getActivityColor(
                      item.type
                    )} rounded-full mr-3 mt-2 flex-shrink-0`}
                  ></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center mb-1">
                      <div className="mr-2 text-gray-400">
                        {getActivityIcon(item.type)}
                      </div>
                      <p className="text-gray-900 font-medium truncate">
                        {item.title}
                      </p>
                    </div>
                    <p className="text-gray-600 text-xs mb-1">
                      {item.description}
                    </p>
                    <p className="text-gray-500 text-xs">
                      {formatTimeAgo(item.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">No recent activity</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
